import { Landmark, NotebookPen } from 'lucide-react';
import { SectionIcon } from '../ui/SectionIcon.js';
import type { FundSummary } from '@nemo/api';
import { chequeDisplay, formatCheque } from '@nemo/core/model';
import { idsForLabels, type FundLists } from '../grid/fundWrites.js';
import type { FundEdits } from './edits.js';
import { InlineText, PaneRow, PickField, Placeholder, StageField } from './fields.js';

export interface PaneProfileProps {
  fund: FundSummary;
  lists: FundLists;
  edits: FundEdits;
}

/**
 * What somebody decided about this fund, as against what the rounds above it
 * show. Every value opens the editor the grid opens for the same column, so
 * there is one vocabulary for stages and sectors rather than two.
 */
export function PaneProfile(props: PaneProfileProps): React.JSX.Element {
  const { fund, lists, edits } = props;
  const cheque = formatCheque(fund);
  const reading = chequeDisplay(fund);

  return (
    <section className="pane-section">
      <h3 className="pane-section-title">
        <SectionIcon icon={Landmark} />
        Profile
      </h3>

      <PaneRow label="Stages">
        <StageField fund={fund} onCommit={edits.stages} />
      </PaneRow>

      <PaneRow label="Sectors">
        <PickField
          multiple
          label={`Sectors ${fund.name} invests in`}
          options={lists.sectors}
          selectedIds={idsForLabels(fund.sectors, lists.sectors)}
          onCommit={edits.sectors}
        >
          {fund.sectors.length === 0 ? <Placeholder /> : fund.sectors.join(', ')}
        </PickField>
      </PaneRow>

      <PaneRow label="Typical cheque">
        <InlineText
          label={`What ${fund.name} typically writes`}
          value={cheque}
          display={
            reading === null ? (
              <Placeholder />
            ) : (
              <span title={reading.detail}>
                {reading.range}
                {reading.qualifier === '' ? null : (
                  <span className="pane-source"> {reading.qualifier}</span>
                )}
              </span>
            )
          }
          onCommit={edits.cheque}
        />
      </PaneRow>

      <PaneRow label="Notable investments">
        <InlineText
          label={`What ${fund.name} is known for`}
          value={fund.notableInvestments.join(', ')}
          display={
            fund.notableInvestments.length === 0 ? (
              <Placeholder />
            ) : (
              fund.notableInvestments.join(', ')
            )
          }
          onCommit={edits.notableInvestments}
        />
      </PaneRow>
    </section>
  );
}

/**
 * What a person wrote about the fund. It is always open rather than clicked
 * into: a note is the one value somebody arrives meaning to add, and it saves
 * on the way out the way every other editor here does.
 */
export function PaneNotes(props: { fund: FundSummary; edits: FundEdits }): React.JSX.Element {
  return (
    <section className="pane-section">
      <h3 className="pane-section-title">
        <SectionIcon icon={NotebookPen} />
        Notes
      </h3>
      <textarea
        // Keyed on the fund: the arrow keys move the pane from one fund to the
        // next without unmounting it, and an uncontrolled textarea would carry
        // the last fund's note across.
        key={props.fund.id}
        className="pane-notes"
        rows={3}
        aria-label={`Notes about ${props.fund.name}`}
        placeholder="Add a note"
        defaultValue={props.fund.notes ?? ''}
        onBlur={(event) => props.edits.notes(event.target.value)}
      />
    </section>
  );
}
